import { cn } from '@/lib/infra/utils';
import { LIFECYCLE_LABEL, RECEIPT_LABEL } from '@/lib/polymarketUs/portfolioModel';
import { usOrderReason, usOrderReasonSentence } from '@/lib/polymarketUs/reason';
import type { UsDraft, UsOrderRow } from '@/lib/polymarketUs/types';
import { durationLabel } from '@/lib/polymarketUs/ticketModel';
import { ORDER_RESULT_SEVERITY_CLASS } from '@/views/ValueDashboard/OrderResultBanner';
import type { UsTicketResult } from './ticketAdapter';
import { usReceiptView } from './usReceiptPresentation';

const reasonOf = (receipt: NonNullable<UsTicketResult['receipt']>) => ('reason' in receipt ? receipt.reason : null);
const orderIdOf = (receipt: NonNullable<UsTicketResult['receipt']>) => ('orderId' in receipt ? receipt.orderId : null);

type Props = {
  result: UsTicketResult;
  /** The exact draft this submission sent, so the receipt stays attributable after later edits. */
  draft: UsDraft;
  /** The market's own name for the outcome and its question, as shown when it was sent. */
  outcomeName?: string | null;
  question?: string | null;
  /** The latest read of this submission's intent, when the ticket follows it. */
  followed?: UsOrderRow | null;
};

/**
 * #1247 — the Game Detail ticket's receipt, in the K/P result slot's shape:
 * compact by Place Order, the state first (it follows the order's lifecycle),
 * then the order. Uncertain exposure adds an explicit visible instruction;
 * it must never be clipped to preserve geometry. Question, exact terms and ids
 * live in keyboard/touch-accessible Details.
 */
export function UsReceiptLine({ result, draft, outcomeName, question, followed }: Props) {
  const view = usReceiptView(result, draft, { outcomeName, question, followed });
  const polite = view.severity === 'success' || view.severity === 'pending';
  const uncertain = view.state === 'unknown' || view.state === 'unconfirmed' || view.state === 'conflict';
  const word = view.word.split(' — ')[0];
  const instruction = uncertain ? 'Check status before placing again.'
    : view.state === 'ready' ? 'Check saved requests before placing again.'
      : view.state === 'replaced' ? 'Check your orders; replacement is reconciling.'
        : result.lookupReason && !followed ? 'Latest status unavailable; check your orders.' : null;
  return (
    <details
      data-testid="us-ticket-receipt"
      data-status={view.status}
      data-state={view.state}
      data-severity={view.severity}
      role={polite ? 'status' : 'alert'}
      aria-live={polite ? 'polite' : undefined}
      className={cn('mt-2 min-w-0 max-w-full rounded-md px-2.5 py-1.5 text-xs leading-4',
        ORDER_RESULT_SEVERITY_CLASS[view.severity])}
    >
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 items-baseline gap-1">
          <strong data-testid="us-ticket-receipt-status" className="shrink-0 font-semibold">{word}:</strong>
          <span data-testid="us-ticket-receipt-order" className="min-w-0 flex-1 truncate">{view.attempt}</span>
          <span className="shrink-0 text-[10px] underline underline-offset-2">Details</span>
        </span>
        {/* A possible live order must never be hidden in a tooltip or truncated. */}
        {instruction && <span className="block break-words">{instruction}</span>}
      </summary>
      <p className="mt-1 whitespace-pre-line break-words [overflow-wrap:anywhere]">{view.reference}</p>
    </details>
  );
}

/**
 * One submission's receipt on the standalone US page. A saved request UUID is
 * shown as a request, never as an intent: with `receipt: null` the server
 * intent is not known yet. Once the ticket reads the order back, its status
 * and next step follow that read.
 */
export function UsTicketReceipt({ result, draft, outcomeName, question, followed }: Props) {
  const { receipt, requestKey, lookupReason } = result;
  const reason = receipt ? reasonOf(receipt) : null;
  const orderId = receipt ? orderIdOf(receipt) : null;
  const view = usReceiptView(result, draft, { outcomeName, question, followed });
  const read = followed && receipt && followed.intentId === receipt.intentId ? followed : null;
  const status = read?.lifecycle ? LIFECYCLE_LABEL[read.lifecycle.state]
    : read ? RECEIPT_LABEL[read.receipt.status] : receipt ? RECEIPT_LABEL[receipt.status] : 'Result unconfirmed';
  return (
    <section
      aria-label="Last submission"
      aria-live="polite"
      data-testid="us-ticket-receipt"
      data-status={receipt?.status ?? 'unconfirmed'}
      data-state={view.state}
      className="border-border min-w-0 rounded border p-3 text-xs"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold">Last submission</h3>
        <strong data-testid="us-ticket-receipt-status">{status}</strong>
      </div>
      <p className="mt-1 break-words [overflow-wrap:anywhere]">
        {draft.target.label} · {draft.action.toUpperCase()} {draft.quantity} contracts at ${draft.outcomePrice} per{' '}
        {draft.target.outcome.toUpperCase()} · {durationLabel(draft.timeInForce)}
        {draft.goodTillTime ? ` until ${draft.goodTillTime}` : ''} · Post-only {draft.postOnly ? 'on' : 'off'}
      </p>
      {receipt ? (
        <p className="mt-1 break-all font-mono" data-testid="us-ticket-receipt-identity">
          Intent {receipt.intentId} · Venue order {orderId ?? read?.orderId ?? 'Unavailable'}
        </p>
      ) : (
        <p className="mt-1 break-words" data-testid="us-ticket-receipt-identity">
          The server intent is not known yet. Your request was saved as{' '}
          <span className="break-all font-mono">request {requestKey}</span>; it is not an intent ID.
        </p>
      )}
      {reason && (
        <p className="mt-1 break-words [overflow-wrap:anywhere]" data-testid="us-ticket-receipt-reason" title={reason}>
          Reason: {usOrderReasonSentence(reason, result.limit)}
        </p>
      )}
      {/* The next step for the latest state; a lost lookup's note has its own line below. */}
      <p className="text-muted-foreground mt-1">{view.nextStep}</p>
      {lookupReason && !read && (
        <p role="status" className="mt-1 break-words" data-testid="us-ticket-receipt-lookup" title={lookupReason}>
          Latest lookup unconfirmed: {usOrderReason(lookupReason)}.{receipt ? ' The recorded result above is retained.' : ''}
        </p>
      )}
    </section>
  );
}
